/**
 * Get the type of a value for JSON display
 */
export function getValueType(value) {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return 'undefined';
  }
  if (Array.isArray(value)) {
    return 'array';
  }
  if (typeof value === 'function') {
    return 'function';
  }
  return typeof value;
}

/**
 * Check if a value can be expanded (has children)
 */
export function isExpandable(value) {
  const type = getValueType(value);
  if (type === 'object') {
    return Object.keys(value).length > 0;
  }
  if (type === 'array') {
    return value.length > 0;
  }
  return false;
}

/**
 * Format a value for display
 */
export function formatValue(value, type) {
  if (type === 'string') {
    return `"${value}"`;
  }
  if (type === 'null') {
    return 'null';
  }
  if (type === 'undefined') {
    return 'undefined';
  }
  if (type === 'function') {
    const name = value.name;
    return name ? `[Function: ${name}]` : '[Function]';
  }
  if (type === 'object') {
    return '{}';
  }
  if (type === 'array') {
    return '[]';
  }
  return String(value);
}

/**
 * Get item count for arrays and objects
 */
export function getItemCount(value) {
  if (Array.isArray(value)) {
    return value.length;
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value).length;
  }
  return undefined;
}

/**
 * Convert data to tree structure
 */
export function convertToTreeData(
  value,
  key,
  path = 'root',
  depth = 0,
  displayFunctions = 'as-string',
  seen = new WeakSet()
) {
  const type = getValueType(value);

  if (depth > 50) {
    return {
      value: path,
      label: key || 'root',
      nodeData: { type: 'string', value: '[Max depth reached]', key, path, depth },
    };
  }

  if (value !== null && typeof value === 'object') {
    if (seen.has(value)) {
      return {
        value: path,
        label: key || 'root',
        nodeData: { type: 'string', value: '[Circular]', key, path, depth },
      };
    }
    seen.add(value);
  }

  if (type === 'function') {
    if (displayFunctions === 'hide') {
      return null;
    }
    if (displayFunctions === 'as-string') {
      return {
        value: path,
        label: key || 'root',
        nodeData: { type, value, key, path, depth },
      };
    }
    const functionProps = Object.getOwnPropertyNames(value).reduce((acc, prop) => {
      acc[prop] = value[prop];
      return acc;
    }, {});
    return convertToTreeData(functionProps, key, path, depth, displayFunctions, seen);
  }

  const expandable = isExpandable(value);
  const nodeValue = path;

  if (!expandable) {
    return {
      value: nodeValue,
      label: key || 'root',
      nodeData: { type, value, key, path, depth },
    };
  }

  const totalCount = type === 'array' ? value.length : Object.keys(value).length;
  const maxChildren = 100;
  const truncated = totalCount > maxChildren;

  const entries = type === 'array'
    ? value.slice(0, maxChildren).map((item, index) => [String(index), item])
    : Object.keys(value).slice(0, maxChildren).map(k => [k, value[k]]);

  const children = entries
    .map(([k, v]) => convertToTreeData(v, k, `${path}.${k}`, depth + 1, displayFunctions, seen))
    .filter((node) => node !== null);

  if (truncated) {
    children.push({
      value: `${path}.__truncated_sentinel__`,
      label: `... ${totalCount - maxChildren} more items`,
      nodeData: {
        type: 'string',
        value: `... ${totalCount - maxChildren} more items`,
        key: null,
        path: `${path}.__truncated_sentinel__`,
        depth: depth + 1,
      },
    });
  }

  return {
    value: nodeValue,
    label: key || 'root',
    children,
    nodeData: { type, value, key, path, itemCount: totalCount, depth },
  };
}
